/** --------------------------------------------------------------------------------------------------------------------
 * @file Student.Administrator.tsx
 * @fileoverview this file will return stepOne one of create new Service/Student only as role admin
 * @updatedBy Arash Goharrostami
 * @updatedAt 2025-06-21 / Saturday - June 21, 2025
 */

import React from 'react';

import api     from '../../../../../../api';
import values  from '../../../../../../values';
import Context from '../../../../../../context';

import CustomDropDownSearch from '../../../../../../components/CustomDropDownSearch';

import { useQuery                                             } from 'react-query';
import { useTranslation                                       } from 'react-i18next';
import { ICreateStudent                                       } from '../../../../../../types/student.interface';
import { ICompanyItem, IOnSearchUser                          } from '../../../../../../types/common.interface';
import { CustomView, CustomButton2, CustomDropdownMultiSearch } from '../../../../../../components';

type Props = {
  defaultValues: ICreateStudent;
  onSubmit: (event: ICreateStudent) => void;
};

/** --------------------------------------------------------------------------------------------------------------------
 * @description
 *   this function provide local values, local function and local API call's depends on need, also have some dependensies
 *   to some lib's too
 * @param props
 * @constructor
 */
const AdministratorStudentRelations: React.FC<Props> = (props: Props): React.ReactElement => {
  /** ------------------------------------------------------------------------------------------------------------------
   * @define local values validation
   */
  const { isLoading } = React.useContext(Context.StudentScreen);

  const [selectedCity        , setSelectedCity        ] = React.useState<any>(props?.defaultValues?.city        );
  const [selectedArea        , setSelectedArea        ] = React.useState<any>(props?.defaultValues?.area        );
  const [selectedParent      , setSelectedParen       ] = React.useState<any>(props?.defaultValues?.parent      );
  const [selectedDriver      , setSelectedDriver      ] = React.useState<any>(props?.defaultValues?.driver      );
  const [selectedSchool      , setSelectedSchool      ] = React.useState<any>(props?.defaultValues?.school      );
  const [selectedCompany     , setSelectedCompany     ] = React.useState<any>(props?.defaultValues?.company     );
  const [selectedProvince    , setSelectedProvince    ] = React.useState<any>(props?.defaultValues?.province    );
  const [selectedNeighborhood, setSelectedNeighborhood] = React.useState<any>(props?.defaultValues?.neighborhood);

  const [searchedValueParent , setSearchedValueParent ] = React.useState<IOnSearchUser>();
  const [searchedValueSchool , setSearchedValueSchool ] = React.useState<IOnSearchUser>();
  const [searchedValueDriver , setSearchedValueDriver ] = React.useState<IOnSearchUser>();
  const [searchedValueCompany, setSearchedValueCompany] = React.useState<IOnSearchUser>();

  const [dropDownErrors, setDropDownErrors] = React.useState<any>({
    neighborhood: '',
    province: '',
    parent: '',
    city: '',
    area: '',
  });

  /** ------------------------------------------------------------------------------------------------------------------
   * @define tihs part of this function would call REST.API's
   */
  const getAllProvince = useQuery([values.useQueryKeys.getAllProvince], () =>
    api.get.common.getAllProvince()
  );
  const getAllCompany = useQuery(
    [values.useQueryKeys.getAllCompany, searchedValueCompany?.searchedValue],
    () =>
      api.post.administrator.getAllCompany({
        search: {base: searchedValueCompany?.base?.value, value: searchedValueCompany?.searchedValue},
      })
  );
  const getAllParent = useQuery(
    [values.useQueryKeys.getAllParent, searchedValueParent],
    () =>
      api.post.administrator.getAllParent({
        search: {
          base: searchedValueParent?.base?.value,
          value: searchedValueParent?.searchedValue,
        },
      })
  );
  const getAllDriver = useQuery(
    [values.useQueryKeys.getAllDriver, selectedCompany?._id, searchedValueDriver?.searchedValue],
    () =>
      api.post.common.findDriver({
        companyId: selectedCompany?._id,
        search: {
          base: searchedValueDriver?.base.value,
          value: searchedValueDriver?.searchedValue,
        },
      })
  );
  const getAllSchool = useQuery(
    [values.useQueryKeys.getAllSchool, searchedValueSchool?.searchedValue],
    () =>
      api.post.common.findSchoolPost({
        search: {
          base: searchedValueSchool?.base?.value,
          value: searchedValueSchool?.searchedValue,
        },
      })
  );
  const getAllCity = useQuery(
    [values.useQueryKeys.getAllCity, selectedProvince],
    () => api.get.common.getAllCity({provinceCode: selectedProvince?.ProvinceCode,})
  );
  const getAllArea = useQuery(
    [values.useQueryKeys.locationGetArea, selectedCity],
    () => api.get.common.searchArea({city: selectedCity?._id, limit: 100}),
    {enabled: !!selectedCity}
  )
  const getAllNeighboarhood = useQuery(
    [values.useQueryKeys.locationGetNeighborhood, selectedArea],
    () => api.get.common.searchNeighborhood({town: selectedArea?._id, limit: 100}),
    {enabled: !!selectedArea}
  )

  const { t } = useTranslation();

  // ?? ===========================================================================================================
  // ??                                           Use Formik Function
  // ?? -----------------------------------------------------------------------------------------------------------

  const submitHandler = () => {
    let dropDownErrorsList = {
      neighborhood: '',
      province: '',
      parent: '',
      city: '',
      area: '',
    };
    if (!selectedCity        ) dropDownErrorsList = {...dropDownErrorsList, city        : t("city_is_required"        )}
    if (!selectedArea        ) dropDownErrorsList = {...dropDownErrorsList, area        : t("area_is_required"        )}
    if (!selectedParent      ) dropDownErrorsList = {...dropDownErrorsList, parent      : t("parent_is_required"      )}
    if (!selectedProvince    ) dropDownErrorsList = {...dropDownErrorsList, province    : t("province_is_required"    )}
    if (!selectedNeighborhood) dropDownErrorsList = {...dropDownErrorsList, neighborhood: t("neighborhood_is_required")}

    setDropDownErrors(dropDownErrorsList);

    if (
      selectedCity      &&
      selectedArea      &&
      selectedParent    &&
      selectedProvince  &&
      selectedNeighborhood
    ) {
      props.onSubmit({
        city        : selectedCity        ,
        area        : selectedArea        ,
        school      : selectedSchool      ,
        parent      : selectedParent      ,
        driver      : selectedDriver      ,
        company     : selectedCompany     ,
        province    : selectedProvince    ,
        neighborhood: selectedNeighborhood,
        cycle: { _id: "0", name: "days_30", value: 30 },
      });
    }
  };

  const AllParentWithCompleteName = getAllParent?.data?.result?.map(
    (item: any) => {return {...item, completeName: item?.firstName + " " + item?.lastName,};}
  );

  const AllDriverWithCompleteName = getAllDriver?.data?.result?.map(
    (item: any) => {return {...item, completeName: item?.firstName + " " + item?.lastName,};}
  );

  const companyInformationWithCompanyName = getAllCompany?.data?.result?.map(
    (item: any) => ({...item, companyName: item?.companyInformation?.companyName,})
  );

  /** ------------------------------------------------------------------------------------------------------------------
   * @return React.ReactElement JSX.Element
   */
  return (
    <form onSubmit={submitHandler} style={{ width: "100%" }}>
      <CustomView.Container className="create-form-content">
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("parent")}</label>
            <CustomDropdownMultiSearch
              isSearch
              baseSearch={values.dropdownBaseSearch.PARENT_BASE_SEARCH.map(
                (item) => ({ ...item, title: t(item.title as string) })
              )}
              items={AllParentWithCompleteName}
              itemName="completeName"
              onSelectItem={(event: ICompanyItem) => {
                setSelectedParen(event);
                setDropDownErrors({ ...dropDownErrors, parent: "" });
              }}
              defaultItem={selectedParent}
              searchedValue={searchedValueParent}
              setSearchedValue={setSearchedValueParent}
              placeHolder={t("selectParent")}
              isLoading={getAllParent.isLoading}
              selectedBaseSearch={searchedValueParent?.base?.value}
            />
            <div className="error">
              {dropDownErrors.parent !== "" && dropDownErrors.parent}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("school")}</label>
            <CustomDropdownMultiSearch
              isSearch
              baseSearch={values.dropdownBaseSearch.SCHOOL_BASE_SEARCH.map(
                (item) => ({ ...item, title: t(item.title as string) })
              )}
              items={getAllSchool?.data?.result}
              itemName="name"
              onSelectItem={(event: ICompanyItem) => setSelectedSchool(event)}
              defaultItem={selectedSchool}
              searchedValue={searchedValueSchool}
              setSearchedValue={setSearchedValueSchool}
              placeHolder={t("selectSchool")}
              isLoading={getAllSchool.isLoading}
              selectedBaseSearch={searchedValueSchool?.base?.value}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("company")}</label>
            <CustomDropdownMultiSearch
              isSearch
              baseSearch={values.dropdownBaseSearch.COMPANY_BASE_SEARCH.map(
                (item) => ({ ...item, title: t(item.title as string) })
              )}
              items={companyInformationWithCompanyName}
              itemName="companyName"
              onSelectItem={(event: ICompanyItem) => setSelectedCompany(event)}
              defaultItem={selectedCompany}
              searchedValue={searchedValueCompany}
              setSearchedValue={setSearchedValueCompany}
              placeHolder={t("selectCompany")}
              isLoading={getAllCompany.isLoading}
              selectedBaseSearch={
                searchedValueCompany?.base?.value === "nationalCode"
                  ? "companyInformation.nationalCode"
                  : searchedValueCompany?.base?.value
              }
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("driver")}</label>
            <CustomDropdownMultiSearch
              isSearch
              baseSearch={values.dropdownBaseSearch.DRIVER_BASE_SEARCH.map(
                (item) => ({ ...item, title: t(item.title as string) })
              )}
              items={AllDriverWithCompleteName}
              itemName="completeName"
              onSelectItem={(event: ICompanyItem) => setSelectedDriver(event)}
              defaultItem={selectedDriver}
              searchedValue={searchedValueDriver}
              setSearchedValue={setSearchedValueDriver}
              placeHolder={t("selectDriver")}
              isLoading={getAllDriver.isLoading}
              selectedBaseSearch={searchedValueDriver?.base?.value}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("province")}</label>
            <CustomDropDownSearch
              isSearch
              items={getAllProvince?.data?.result}
              itemName="Name"
              onSelectItem={(event: any): void => {
                setSelectedProvince(event)
                setDropDownErrors({ ...dropDownErrors, province: '' });
              }}
              placeHolder={t("selectProvince")}
              defaultItem={selectedProvince}
              isLoading={getAllProvince.isLoading}
            />
            <div className="error">{dropDownErrors.province !== "" && dropDownErrors.province}</div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("city")}</label>
            <CustomDropDownSearch
              isSearch
              items={getAllCity?.data?.result}
              itemName="Name"
              onSelectItem={(event: any): void => {
                setSelectedCity(event);
                setDropDownErrors({ ...dropDownErrors, city: '' });
              }}
              placeHolder={t("selectCity")}
              defaultItem={selectedCity}
              isLoading={getAllCity.isLoading}
            />
            <div className="error">{dropDownErrors.city !== "" && dropDownErrors.city}</div>
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("area")}</label>
            <CustomDropDownSearch
              isSearch
              items={getAllArea?.data?.result?.docs}
              itemName="name"
              onSelectItem={(event: any) => {
                setSelectedArea(event);
                setDropDownErrors({ ...dropDownErrors, area: '' });
              }}
              placeHolder={t("selectArea")}
              defaultItem={selectedArea}
              isLoading={getAllArea.isLoading}
            />
            <div className="error">{dropDownErrors.area !== "" && dropDownErrors.area}</div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("neighborhood")}</label>
            <CustomDropDownSearch
              isSearch
              items={getAllNeighboarhood?.data?.result?.docs}
              itemName="name"
              onSelectItem={(event: any) => {
                setSelectedNeighborhood(event);
                setDropDownErrors({ ...dropDownErrors, neighborhood: '' });
              }}
              placeHolder={t("selectNeighborhood")}
              defaultItem={selectedNeighborhood}
              isLoading={getAllCity.isLoading}
            />
            <div className="error">{dropDownErrors.neighborhood !== "" && dropDownErrors.neighborhood}</div>
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
        <CustomView.Column medium={6} className="form-colum submit-button-relation">
            <CustomButton2 isLoading={isLoading} onClick={submitHandler} title={t("next")} />
          </CustomView.Column>
        </CustomView.Row>
      </CustomView.Container>
    </form>
  );
};

export default AdministratorStudentRelations;
